<template>
  <div class="space-y-24">
    <header>
      <h2 class="body-2">
        {{ i18n(translations.heading2) }}
      </h2>

      <h1 class="heading-1">
        {{ i18n(translations.heading1) }}
      </h1>
    </header>

    <RevInfoBlock
      v-if="error"
      :cta="i18n(translations.tryAgain)"
      :icon="IconWarning"
      :title="i18n(translations.error)"
      variant="danger"
      @click="() => refresh()"
    />

    <div class="space-y-16">
      <DefectWarrantyInfoBlock
        v-if="coverage && hasOngoingDefectWarranty"
        :backmarket-warranty="coverage.backmarketWarranty"
        :merchant-warranty="coverage.merchantWarranty"
      />

      <RevForm :initial-values @submit="withdraw">
        <template #default="{ values }">
          <div class="space-y-16">
            <PolicyCheckbox
              v-for="policy in cancellablePolicies"
              :key="policy.id"
              v-model="values[policy.id]"
              :policy
            />

            <RevSkeleton
              v-if="status === 'pending'"
              height="3.625rem"
              shape="rectangle"
              width="100%"
            />

            <RevButton
              :disabled="
                isSubmitting || getSelectedPolicies(values).length === 0
              "
              :loading="isSubmitting"
              :tracking="{
                name: 'withdraw-policies',
                orderlineId: getSelectedPolicies(values).map(
                  (policy) => policy.id,
                ),
              }"
              type="submit"
              variant="primary"
            >
              {{ i18n(translations.confirm) }}
            </RevButton>
          </div>
        </template>
      </RevForm>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAsyncData, useRoute } from '#imports'
import { computed, ref } from 'vue'

import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevForm } from '@ds/components/Form'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevSkeleton } from '@ds/components/Skeleton'
import { IconWarning } from '@ds/icons/IconWarning'

import translations from './PolicySelectionForm.translations'
import DefectWarrantyInfoBlock from './components/DefectWarrantyInfoBlock/DefectWarrantyInfoBlock.vue'
import PolicyCheckbox from './components/PolicyCheckbox/PolicyCheckbox.vue'

const i18n = useI18n()
const route = useRoute()
const logger = useLogger()
const { openErrorToast } = useTheToast()

type InsurancePolicy =
  ApiSpecs['/bm/orders/v1/orderline/:orderlineId/coverage']['GET']['response']['insurancePolicies'][number]

const $httpFetch = useHttpFetch()

const {
  data: coverage,
  error,
  status,
  refresh,
} = useAsyncData(async () => {
  return await $httpFetch('/bm/orders/v1/orderline/:orderlineId/coverage', {
    pathParams: {
      orderlineId: route.params.orderlineId.toString(),
    },
  })
})

const isSubmitting = ref(false)
const emit = defineEmits(['submit'])

const hasOngoingDefectWarranty = computed(() => {
  if (!coverage.value) return false

  const { backmarketWarranty, merchantWarranty } = coverage.value

  return [backmarketWarranty, merchantWarranty].some(
    (warranty) => warranty && Date.parse(warranty.endDate) > Date.now(),
  )
})

type FormValues = Record<number | string, boolean>

const cancellablePolicies = computed(() => {
  if (!coverage.value) return []

  return coverage.value.insurancePolicies.filter((policy) => {
    return policy.isCancellable && !policy.hasPendingTerminationRequest
  })
})

const initialValues = computed<FormValues>(() => {
  if (!cancellablePolicies.value) return {}

  const isSelected = cancellablePolicies.value.length === 1

  return cancellablePolicies.value.reduce(
    (acc, policy) => ({ ...acc, [policy.id]: isSelected }),
    {},
  )
})

function getSelectedPolicies(values: FormValues) {
  if (!cancellablePolicies.value) return []

  return cancellablePolicies.value.filter((policy) => values[policy.id])
}

async function withdraw(values: FormValues) {
  isSubmitting.value = true

  const selectedPolicies = getSelectedPolicies(values)

  try {
    await Promise.all(
      selectedPolicies.map((policy: InsurancePolicy) =>
        $httpFetch('/api/insurance/v1/policies/:policyId/termination', {
          method: 'POST',
          pathParams: { policyId: policy.id },
        }),
      ),
    )

    emit('submit')
  } catch (e) {
    const err = e as Error
    err.cause = selectedPolicies

    logger.error('[INSURANCE][API][TERMINATION] Error terminating policies', {
      error: err,
      owners: ['bot-squad-extended-services-front'],
    })
    openErrorToast()
  } finally {
    isSubmitting.value = false
  }
}
</script>
